import React, { useMemo } from "react";
import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";


interface PasswordStrengthIndicatorProps {
  password: string;
}

const rules = [
  { label: "8 caractères minimum", test: (p: string) => p.length >= 8 },
  { label: "Une lettre majuscule", test: (p: string) => /[A-Z]/.test(p) },
  { label: "Une lettre minuscule", test: (p: string) => /[a-z]/.test(p) },
  { label: "Un chiffre", test: (p: string) => /[0-9]/.test(p) },
  { label: "Un caractère spécial", test: (p: string) => /[^A-Za-z0-9]/.test(p) },
];

export function PasswordStrengthIndicator({ password }: PasswordStrengthIndicatorProps) {
  const results = useMemo(
    () => rules.map((r) => ({ ...r, passed: r.test(password) })),
    [password]
  );

  const passedCount = results.filter((r) => r.passed).length;
  const strength = (passedCount / rules.length) * 100;

  const strengthLabel = useMemo(() => {
    if (passedCount <= 1) return { text: "Très faible", color: "text-red-500" };
    if (passedCount <= 2) return { text: "Faible", color: "text-orange-500" };
    if (passedCount <= 3) return { text: "Moyen", color: "text-yellow-500" };
    if (passedCount <= 4) return { text: "Fort", color: "text-emerald-500" };
    return { text: "Très fort", color: "text-emerald-600" };
  }, [passedCount]);

  const barColor = useMemo(() => {
    if (passedCount <= 1) return "bg-red-500";
    if (passedCount <= 2) return "bg-orange-500";
    if (passedCount <= 3) return "bg-yellow-500";
    if (passedCount <= 4) return "bg-emerald-500";
    return "bg-emerald-600";
  }, [passedCount]);

  if (!password) return null;

  return (
    <div className="space-y-2.5 -mt-1">
      {/* Strength bar */}
      <div className="space-y-1">
        <div className="flex justify-between items-center">
          <span className="text-[11px] text-muted-foreground">Force du mot de passe</span>
          <span className={cn("text-[11px] font-medium", strengthLabel.color)}>
            {strengthLabel.text}
          </span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
          <div
            className={cn("h-full rounded-full transition-all duration-300 ease-out", barColor)}
            style={{ width: `${strength}%` }}
          />
        </div>
      </div>

      {/* Rules checklist */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
        {results.map((r) => (
          <div key={r.label} className="flex items-center gap-1.5">
            {r.passed ? (
              <Check className="h-3 w-3 text-emerald-500 shrink-0" />
            ) : (
              <X className="h-3 w-3 text-muted-foreground/50 shrink-0" />
            )}
            <span
              className={cn(
                "text-[11px] leading-tight",
                r.passed ? "text-emerald-600" : "text-muted-foreground/70"
              )}
            >
              {r.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
